// Display-only reconstruction of the policy cost math. Rows come straight from
// `ltcPolicy.investmentCashFlows` — the per-year premium cash flows the funding
// engine computes in `convertPolicyPremiumsToCashFlows` and validates against
// `policyTotalCost` (`investmentCashflowEqualsPolicyTotalCost`). Nothing here
// re-derives cost; this module only classifies each engine cashflow (lump sum /
// full-year premium / partial final year) and annotates it with the inputs that
// produced it, so the table can show a per-row formula that reconstructs
// `policyTotalCost = monthlyPremiumTotalCost + policyLumpSumPayment`.
//
// Engine booking conventions this mirrors (see calcFundingSourceDerivatives):
//   • The lump sum is booked at `policyPremiumStartYear` (the issue year).
//   • Recurring premiums begin the calendar year AFTER issue (an ROI/IRR
//     booking convention) — payment year 1 is booked at startYear + 1.
//   • Each full payment year is `monthly × 12`; when the pay period is not a
//     whole number of years, a final partial bucket covers the EXACT
//     fractional remaining months (the engine does not round it — the stream
//     sum IS `policyTotalCost`).
//   • With an annual increase rate, step-ups are clamped to whole pay years:
//     `stepUpYears = max(0, min(floor(appliedIncreaseYears), fullYears))`.
//     Payment year n uses `monthly × (1 + rate)^min(n − 1, stepUpYears)`; the
//     partial bucket bills at exponent = stepUpYears — one anniversary step-up
//     above the last whole year's rate when stepUpYears === fullYears,
//     otherwise the held last whole-year rate.

export interface PremiumCashflowRow {
  /** 1-based ordinal of the recurring payment year; null for the lump sum. */
  paymentYear: number | null;
  /** Calendar year the engine booked this cashflow at (raw `CashFlow.year`). */
  calendarYear: number;
  /**
   * Calendar year in the POLICY-YEAR framing the rest of projection-details
   * uses (policy year 1 = issue year, so year N = startYear + N − 1). The
   * engine books recurring premiums one calendar year later for ROI/IRR
   * (`calendarYear` above); displaying that raw booking would put this table
   * one year ahead of the Policy Growth / monthly tables for the same policy
   * year. The lump sum is booked at issue, so both framings agree there.
   */
  displayCalendarYear: number;
  kind: 'lump' | 'annual' | 'partial';
  /** Months this bucket covers — 12, the partial remainder, or null for lump. */
  monthsCovered: number | null;
  /** amount / monthsCovered for premium rows; null for the lump sum. */
  monthlyRate: number | null;
  /**
   * Compounding exponent applied to the base monthly premium for this bucket
   * (0 when no increase rate / first year). Null for the lump sum.
   */
  increaseExponent: number | null;
  amount: number;
  /** Running total paid through this row, inclusive. */
  cumulative: number;
}

export interface PremiumScheduleInputs {
  monthlyCost: number | null;
  lumpSum: number | null;
  startYear: number | null;
  periodMonths: number | null;
  limitedPayYears: number | null;
  increaseRate: number | null;
  appliedIncreaseYears: number | null;
}

export interface PremiumCashflowSchedule {
  /** True when the policy has any premium cashflow to display. */
  hasAny: boolean;
  rows: PremiumCashflowRow[];
  /** Σ row amounts — what the table's Total row shows. */
  totalPaid: number;
  /** The engine's canonical total (recurring total + lump sum). */
  policyTotalCost: number | null;
  monthlyPremiumTotalCost: number | null;
  inputs: PremiumScheduleInputs;
  /**
   * Whether Σ cashflows matches `policyTotalCost` within the engine's own
   * validation tolerance (`investmentCashflowEqualsPolicyTotalCost` flags
   * > 1% relative difference; a $1 floor covers tiny totals). Null when the
   * engine total is missing. The engine now derives `policyTotalCost` by
   * summing this exact stream, so a freshly calculated policy matches to the
   * cent — any drift means the stored total predates the latest calculation.
   */
  reconciles: boolean | null;
  /**
   * Whole step-up years actually applied when an increase rate is set —
   * `max(0, min(floor(appliedIncreaseYears), fullYears))`, mirroring the
   * engine's `stepUpYears`. Null when there is no increase rate.
   */
  stepUpYears: number | null;
}

export function buildPremiumCashflowSchedule(
  policy: PolicyFundingSource | null | undefined,
): PremiumCashflowSchedule {
  const cashFlows = policy?.investmentCashFlows ?? [];
  const startYear = policy?.policyPremiumStartYear ?? null;
  const periodMonths = policy?.premiumInvestmentPeriodMonths ?? null;
  const monthlyCost = policy?.policyPremiumMonthlyCost ?? null;
  const increaseRate = policy?.policyPremiumIncreaseRate ?? null;

  const inputs: PremiumScheduleInputs = {
    monthlyCost,
    lumpSum: policy?.policyLumpSumPayment ?? null,
    startYear,
    periodMonths,
    limitedPayYears: policy?.policyLimitedPayYears ?? null,
    increaseRate,
    appliedIncreaseYears: policy?.appliedPolicyPremiumIncreaseYears ?? null,
  };

  // Reconstruct the engine's bucket layout: full years are 12 months; the
  // final bucket is the EXACT fractional remainder when the pay period isn't a
  // whole number of years (no rounding — the engine's stream sum IS
  // `policyTotalCost`). The 1e-6 floor ignores floating-point dust. Mirrors
  // `convertPolicyPremiumsToCashFlows`.
  const fullYears =
    periodMonths != null && periodMonths > 0 ? Math.floor(periodMonths / 12) : null;
  const remainingMonths =
    periodMonths != null && fullYears != null ? periodMonths - fullYears * 12 : 0;
  const firstPremiumYear = startYear != null ? startYear + 1 : null;
  const partialBucketYear =
    firstPremiumYear != null && fullYears != null && remainingMonths > 1e-6
      ? firstPremiumYear + fullYears
      : null;
  // Step-ups are clamped to [0, fullYears] whole years (the engine floors a
  // fractional applied-increase period and zero-clamps negative stale data):
  // payment year n compounds the base monthly premium by min(n − 1,
  // stepUpYears); the partial bucket bills at exponent = stepUpYears (one
  // step-up above the last whole year when the step-ups run the whole pay
  // period, otherwise the held last whole-year rate).
  const hasIncrease = increaseRate != null && increaseRate > 0;
  const stepUpYears = Math.max(
    0,
    Math.min(Math.floor(inputs.appliedIncreaseYears ?? fullYears ?? 0), fullYears ?? 0),
  );

  const sorted = [...cashFlows].sort((a, b) => a.year - b.year);
  // The partial bucket is the LAST flow booked at its calendar year, not just
  // any flow at that year: with a fractional applied-increase period the
  // engine's full-year loop can run one extra iteration and emit a full-year
  // flow at the same calendar year as the partial bucket. The engine pushes
  // the partial after the full years and Array.sort is stable, so the final
  // occurrence is the partial.
  const partialRowIndex =
    partialBucketYear != null
      ? sorted.reduce((last, cf, i) => (cf.year === partialBucketYear ? i : last), -1)
      : -1;
  let cumulative = 0;
  const rows: PremiumCashflowRow[] = sorted.map((cf, i) => {
    cumulative += cf.amount;
    // The engine books exactly one non-recurring flow: the lump sum at the
    // issue year. Everything at startYear + 1 onward is a premium bucket.
    const isLump = startYear != null && cf.year === startYear;
    if (isLump || firstPremiumYear == null) {
      return {
        paymentYear: null,
        calendarYear: cf.year,
        displayCalendarYear: cf.year,
        kind: 'lump' as const,
        monthsCovered: null,
        monthlyRate: null,
        increaseExponent: null,
        amount: cf.amount,
        cumulative,
      };
    }
    const paymentYear = cf.year - startYear!;
    const isPartial = i === partialRowIndex;
    const monthsCovered = isPartial ? remainingMonths : 12;
    const increaseExponent = hasIncrease
      ? isPartial
        ? stepUpYears
        : Math.min(paymentYear - 1, stepUpYears)
      : 0;
    return {
      paymentYear,
      calendarYear: cf.year,
      // Un-shift the ROI booking offset: payment year N covers policy year N,
      // whose calendar year is startYear + N − 1 = booked year − 1.
      displayCalendarYear: cf.year - 1,
      kind: isPartial ? ('partial' as const) : ('annual' as const),
      monthsCovered,
      monthlyRate: monthsCovered > 0 ? cf.amount / monthsCovered : null,
      increaseExponent,
      amount: cf.amount,
      cumulative,
    };
  });

  const totalPaid = cumulative;
  const policyTotalCost = policy?.policyTotalCost ?? null;

  return {
    hasAny: rows.some(r => r.amount > 0.5),
    rows,
    totalPaid,
    policyTotalCost,
    monthlyPremiumTotalCost: policy?.monthlyPremiumTotalCost ?? null,
    inputs,
    reconciles:
      policyTotalCost != null
        ? Math.abs(totalPaid - policyTotalCost) <= Math.max(1, 0.01 * Math.abs(policyTotalCost))
        : null,
    stepUpYears: hasIncrease ? stepUpYears : null,
  };
}
